import { ApiError } from "@eduviaos/shared";
import type { PortalLocale } from "./i18n/portalShell";

/**
 * The last line of an error message, for surfaces that don't map error codes.
 *
 * The backend's `detail` is written in English and only in English, so passing
 * it straight through — which is what most of the portal did — puts raw
 * English in front of a learner who chose Hindi. Where we know what a status
 * means we say it in their language; where we don't, the caller's own copy is
 * better than a leak.
 *
 * The English detail is still worth showing to an English session: it is
 * usually more specific than any fallback we could write ("This group is
 * full", "Username already taken").
 */
export function apiErrorMessage(
  err: unknown,
  locale: PortalLocale,
  strings: ApiErrorStrings,
  fallback: string,
): string {
  if (!(err instanceof ApiError)) return fallback;

  switch (err.status) {
    case 0:
      return strings.errorOffline;
    case 429:
      return strings.errorTooManyRequests;
    case 503:
      return strings.errorServiceDown;
    default:
      break;
  }

  const detail = err.message.trim();
  if (locale === "en" && detail.length > 0) return detail;
  return fallback;
}

/**
 * Authentication surfaces must never echo server detail, even in English.
 *
 * Login, invite, and magic-link endpoints sit on a trust boundary where raw
 * detail can disclose account state or infrastructure. Only the three
 * transport/rate statuses with deliberately generic copy are safe to map.
 */
export function authErrorMessage(err: unknown, strings: ApiErrorStrings, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;

  switch (err.status) {
    case 0:
      return strings.errorOffline;
    case 429:
      return strings.errorTooManyRequests;
    case 503:
      return strings.errorServiceDown;
    default:
      return fallback;
  }
}

/** The subset of the locale table this helper needs. */
export interface ApiErrorStrings {
  errorOffline: string;
  errorTooManyRequests: string;
  errorServiceDown: string;
}
